import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { products } from "@/data/products";

export interface WishlistItem {
  id: string;
  name: string;
  price: number;
  image: string;
  category: string;
  description: string;
}

interface WishlistContextType {
  items: WishlistItem[];
  addToWishlist: (item: WishlistItem) => void;
  removeFromWishlist: (id: string) => void;
  toggleWishlist: (item: WishlistItem) => void;
  isInWishlist: (id: string) => boolean;
  totalItems: number;
  loading: boolean;
}

const WishlistContext = createContext<WishlistContextType | undefined>(undefined);

const getLocalWishlist = (): WishlistItem[] => {
  const saved = localStorage.getItem("bunnie-wishlist");
  return saved ? JSON.parse(saved) : [];
};

const setLocalWishlist = (items: WishlistItem[]) => {
  localStorage.setItem("bunnie-wishlist", JSON.stringify(items));
};

export const WishlistProvider = ({ children }: { children: React.ReactNode }) => {
  const [items, setItems] = useState<WishlistItem[]>(getLocalWishlist);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      syncFromDb();
    } else {
      setItems(getLocalWishlist());
    }
  }, [user]);

  useEffect(() => {
    setLocalWishlist(items);
  }, [items]);

  const syncFromDb = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("wishlist_items")
        .select("product_id")
        .eq("user_id", user.id);

      if (error) throw error;

      const localItems = getLocalWishlist();
      const dbItems: WishlistItem[] = (data || []).map(row => {
        const product = products.find(p => p.id === row.product_id);
        return product ? {
          id: product.id,
          name: product.name,
          price: product.price,
          image: product.image,
          category: product.category,
          description: product.description,
        } : null;
      }).filter(Boolean) as WishlistItem[];

      const merged = [...dbItems];
      for (const localItem of localItems) {
        if (!merged.find(m => m.id === localItem.id)) {
          merged.push(localItem);
          await supabase.from("wishlist_items").upsert({
            user_id: user.id,
            product_id: localItem.id,
          });
        }
      }
      setItems(merged);
    } catch (err) {
      console.error("Wishlist sync error:", err);
    }
    setLoading(false);
  };

  const addToWishlist = useCallback((item: WishlistItem) => {
    setItems(prev => {
      if (prev.find(i => i.id === item.id)) return prev;
      toast.success(`${item.name} added to wishlist ♥`);
      if (user) {
        supabase.from("wishlist_items").upsert({ user_id: user.id, product_id: item.id }).then();
      }
      return [...prev, item];
    });
  }, [user]);

  const removeFromWishlist = useCallback((id: string) => {
    setItems(prev => {
      const item = prev.find(i => i.id === id);
      if (item) toast(`${item.name} removed from wishlist`);
      if (user) {
        supabase.from("wishlist_items").delete().eq("user_id", user.id).eq("product_id", id).then();
      }
      return prev.filter(i => i.id !== id);
    });
  }, [user]);

  const toggleWishlist = useCallback((item: WishlistItem) => {
    setItems(prev => {
      const exists = prev.find(i => i.id === item.id);
      if (exists) {
        toast(`${item.name} removed from wishlist`);
        if (user) {
          supabase.from("wishlist_items").delete().eq("user_id", user.id).eq("product_id", item.id).then();
        }
        return prev.filter(i => i.id !== item.id);
      }
      toast.success(`${item.name} added to wishlist ♥`);
      if (user) {
        supabase.from("wishlist_items").upsert({ user_id: user.id, product_id: item.id }).then();
      }
      return [...prev, item];
    });
  }, [user]);

  const isInWishlist = useCallback((id: string) => items.some(i => i.id === id), [items]);

  return (
    <WishlistContext.Provider value={{ items, addToWishlist, removeFromWishlist, toggleWishlist, isInWishlist, totalItems: items.length, loading }}>
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => {
  const ctx = useContext(WishlistContext);
  if (!ctx) throw new Error("useWishlist must be used within WishlistProvider");
  return ctx;
};
